import React from 'react';
import cx from 'classnames';

import Card, { CardStyles, CardShadow } from '@app/common/components/cards/Card';
import { Tag, TagBorderRadius, TagColor, TagSize } from '@app/common/components/tag/Tag';
import { formatPriceDisplay, getBillingText } from '../../utils/planUtils';
import { TariffPlan } from '@app/types/TariffPlan';
import { Subscription } from '@app/hooks/query/useSubscriptionData';
import { SubscriptionBillingPeriod } from '@app/config';

import styles from './PlanCard.module.scss';
import { AccountType } from '@app/constants/constants';
import { isAgencyUserAccount } from '@app/utils/userUtils';
import PlanInclusions from '@app/components/settings/subscription/PlanInclusions/PlanInclusions';

// Sub-component interfaces
interface PlanTitleProps {
    title: string;
    isCurrentPlan?: boolean;
}


interface PlanPricingProps {
    price: number;
    currency: string;
    billingFrequency: SubscriptionBillingPeriod;
    taxDescription: string;
}

export enum PlanCardVariant {
    SELECTION = 'selection',
    CONFIRMATION = 'confirmation'
}

type InnerPlanCardProps = {
    plan: TariffPlan | Subscription;
    isCurrentPlan?: boolean;
    billingFrequency: SubscriptionBillingPeriod;
    variant?: PlanCardVariant;
    className?: string;
    accountType?: AccountType;
};

const PlanTitle: React.FC<PlanTitleProps> = ({ title, isCurrentPlan }) => (
    <div className={styles.planTitleRow}>
        <h3 className={styles.planTitle}>{title}</h3>
        {isCurrentPlan && (
            <Tag
                text="Current Plan"
                size={TagSize.X_Small}
                color={TagColor.Grey}
                borderRadius={TagBorderRadius.Square}
                className={styles.currentPlanTag}
            />
        )}
    </div>
);


const PlanPricing: React.FC<PlanPricingProps> = ({ price, currency, billingFrequency, taxDescription }) => {
    const priceDisplay = formatPriceDisplay(price);
    const billingText = getBillingText(billingFrequency);

    return (
        <div className={styles.pricingSection}>
            <div className={styles.priceContainer}>
                <div className={styles.priceLeft}>
                    <span className={styles.price}>{priceDisplay}</span>
                </div>
                <div className={styles.priceRight}>
                    <div className={styles.monthlyPrice}>
                        <div className={styles.currency}>{currency}</div>
                        <span className={styles.perMonth}>/mth</span>
                    </div>
                    <div className={styles.gstText}>({taxDescription})</div>
                </div>
            </div>
            <div className={styles.billingText}>{billingText}</div>
        </div>
    );
};

const InnerPlanCard: React.FC<InnerPlanCardProps> = ({
    plan,
    isCurrentPlan,
    billingFrequency,
    variant = PlanCardVariant.SELECTION,
    className,
    accountType
}) => {
    return (
        <Card
            className={cx(
                styles.planCard,
                {
                    [styles.confirmation]: variant === PlanCardVariant.CONFIRMATION,
                    [styles.currentPlan]: isCurrentPlan
                },
                className
            )}
            style={CardStyles.SQUARE}
            shadow={CardShadow.SMALL}
        >
            <div className={styles.planContent}>
                {variant === PlanCardVariant.SELECTION && (
                    <>
                        <div className={styles.planLeftSection}>
                            <PlanTitle title={plan.title} isCurrentPlan={isCurrentPlan} />
                            <PlanInclusions
                                constraintText={plan.constraintText}
                                documentConstraintText={plan.documentConstraintText}
                                accountType={accountType}
                                showDisclaimer={true}
                                className={styles.planInclusions}
                            />
                        </div>

                        <div className={styles.planRightSection}>
                            <PlanPricing
                                price={plan.price}
                                currency={plan.currency}
                                billingFrequency={billingFrequency}
                                taxDescription={plan.taxDescription}
                            />
                        </div>
                    </>
                )}
                {variant === PlanCardVariant.CONFIRMATION && (
                    <>
                        <PlanTitle title={plan.title} isCurrentPlan={isCurrentPlan} />
                        <PlanPricing
                            price={plan.price}
                            currency={plan.currency}
                            billingFrequency={billingFrequency}
                            taxDescription={plan.taxDescription}
                        />
                        <PlanInclusions
                            constraintText={plan.constraintText}
                            documentConstraintText={plan.documentConstraintText}
                            accountType={accountType}
                            showDisclaimer={true}
                            className={styles.planInclusions}
                        />
                    </>
                )}
            </div>
        </Card>
    );
};

type PlanCardProps = {
    onSelect?: () => void;
    disabled?: boolean;
} & InnerPlanCardProps;

const PlanCard: React.FC<PlanCardProps> = ({ onSelect, disabled, ...innerProps }) => {
    if (onSelect) {
        return (
            <button
                className={cx(styles.planButton, {
                    [styles.disabled]: disabled
                })}
                onClick={disabled ? undefined : onSelect}
                disabled={disabled}
                type="button"
            >
                <InnerPlanCard {...innerProps} />
            </button>
        );
    }

    return <InnerPlanCard {...innerProps} />;
};

export default PlanCard;
